import React, { useEffect, useState } from 'react';
import { Props as FormProps } from '../ActionForm';
import { ClientFlowActionStopFlows } from '~/graphql/types.client';
import useIssuesByActionId from '../../../Builder/hooks/useIssuesByActionId';
import useBuilderContext from '../../../Builder/hooks/useBuilderContext';
import MultiSelectInput, {
  type SelectOption,
} from '~/components/molecule/MultiSelectInput';
import InputLabel from '~/components/atom/InputLabel';
import Spinner from '~/components/atom/Icon/components/Spinner';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body } from '~/components/atom/Typography';
import useFlows from '~/hooks/useFlows';

export type Props = FormProps & {
  dataTestId?: string;
  action: ClientFlowActionStopFlows;
};

const text = {
  placeholder: 'Selecteer een flow',
  label: 'Flow',
  loading: 'Flows laden...',
  deletedFlowPlaceholder: 'Verwijderde flow:',
  error: 'Er is iets misgegaan bij het laden van de flows',
  emptyError: 'Er moet minstens één flow geselecteerd worden',
  noFlows:
    'Er zijn nog geen flows om te selecteren. Voeg eerst een flow toe of verwijder deze stap.',
  deletedFlowsError: 'Er zijn geselecteerde flows die niet meer bestaan',
  noRemainingFlows: 'Er zijn geen flows meer om te selecteren.',
};

const Content: React.FCC<{
  action: ClientFlowActionStopFlows;
  onChange: (changes: ClientFlowActionStopFlows) => void;
  existingFlows: Array<{ id: string; name: string }>;
}> = ({ action, onChange, existingFlows }) => {
  const issues = useIssuesByActionId(action.id);

  const deletedFlowsLookup = action.toStopFlowBlueprintIds.reduce<
    Record<string, { id: string; name: string }>
  >((acc, id) => {
    if (!existingFlows.some(flow => flow.id === id)) {
      acc[id] = {
        id,
        name: `${text.deletedFlowPlaceholder} ${id}`,
      };
    }
    return acc;
  }, {});

  const hasDeletedFlows = Object.keys(deletedFlowsLookup).length > 0;

  const allOptions: Array<SelectOption> = [
    ...existingFlows,
    ...Object.values(deletedFlowsLookup),
  ].map(flow => ({
    label: flow.name,
    key: flow.id,
    payload: {
      id: flow.id,
      name: flow.name,
      filterLabel: flow.name,
      tagLabel: flow.name,
    },
    isValid: !deletedFlowsLookup[flow.id],
  }));

  const selectedFlows = allOptions.filter(option =>
    action.toStopFlowBlueprintIds.includes(option.payload.id),
  );

  const validOptions = allOptions.filter(option => option.isValid);

  const [localError, setLocalError] = useState<string | null>(
    issues.length > 0 ? issues[0].message : null,
  );

  const remainingFlows = validOptions.filter(
    option =>
      !action.toStopFlowBlueprintIds.some(
        selectedOption => selectedOption === option.key,
      ),
  );

  useEffect(() => {
    if (action.toStopFlowBlueprintIds.length === 0) {
      setLocalError(text.emptyError);
    } else if (hasDeletedFlows) {
      setLocalError(text.deletedFlowsError);
    } else {
      setLocalError(null);
    }
  }, [action.toStopFlowBlueprintIds, hasDeletedFlows]);

  if (allOptions.length === 0) {
    return (
      <JustificationContainer align="center" gap="xxs">
        <Body size="m" color={{ group: 'primary' }}>
          {text.noFlows}
        </Body>
      </JustificationContainer>
    );
  }

  return (
    <>
      <InputLabel label={text.label} />
      <MultiSelectInput
        options={validOptions}
        selectedOptions={selectedFlows}
        onSelected={option => {
          onChange({
            ...action,
            toStopFlowBlueprintIds: [
              ...action.toStopFlowBlueprintIds,
              option.payload.id,
            ],
          });
        }}
        onRemoved={option => {
          onChange({
            ...action,
            toStopFlowBlueprintIds: action.toStopFlowBlueprintIds.filter(
              id => id !== option.payload.id,
            ),
          });
        }}
        placeholder={
          remainingFlows.length > 0 ? text.placeholder : text.noRemainingFlows
        }
        externalErrors={localError ? [localError] : []}
        disabled={remainingFlows.length === 0}
      />
    </>
  );
};

const StopFlowsWrapper: React.FCC<Props> = ({ action, onChange }) => {
  const { flowBlueprintId } = useBuilderContext();

  const {
    flows: existingFlows,
    loading,
    error,
  } = useFlows({
    excludeFlowIds: [flowBlueprintId],
  });

  if (loading) {
    return (
      <JustificationContainer align="center" gap="xxs">
        <Spinner />
        <Body size="m" color={{ group: 'primary' }}>
          {text.loading}
        </Body>
      </JustificationContainer>
    );
  }

  if (error) {
    return (
      <JustificationContainer align="center" gap="xxs">
        <Body size="m" color={{ group: 'primary' }}>
          {text.error}
        </Body>
      </JustificationContainer>
    );
  }

  return (
    <Content
      action={action}
      onChange={onChange}
      existingFlows={existingFlows}
    />
  );
};

export default StopFlowsWrapper;
